/* eslint-disable react-hooks/exhaustive-deps */
import { Fragment, lazy, useCallback } from "react"
import type { ChangeEvent, FC, LazyExoticComponent, ReactElement } from "react"

import isEmpty from "lodash-es/isEmpty"
import values from "lodash-es/values"
import type { UseFormRegisterReturn } from "react-hook-form"
import { Trans } from "react-i18next"
import { type Location, useLocation } from "react-router-dom"
import { useSessionStorage, useUnmount } from "react-use"

import { ErrorHint, ModalContainer, type TInputProps } from "@/3514/components"
import { EModalId, type TModalDispatcher, useModalDispatcher } from "@/3514/store/modal"
import {
    buildCssClassName,
    getLinkDecorationTwClasses,
    isBadRequestError,
    isInternalServerError,
    isTooManyRequestsError,
    withPressHandler
} from "@/3514/utils"
import type { TSsoModalProps } from "@/components/login/SsoModalBody"
import type { TVerificationModalProps } from "@/components/login/VerificationModalBody"
import { type IUseTranslation, useTranslation } from "@/hooks"
import { EStorageServiceType, LocalStorageServiceKeys, SessionStorageServiceKeys, StorageService } from "@/services"
import { type TEmptyRenderCallback, emptyRenderCallback } from "@/shared/types/functions"

import { type TLoginMutation, type TSsoMutation, useLoginMutation, useSsoMutation } from "./auth.api.mutations.hooks"
import type { TLoginMutationPayload, TLoginMutationResponse, TSsoMutationResponse } from "./auth.api.types"
import { AuthContainer } from "./auth.container"
import {
    type TPostLoginFunction,
    useErrorLoginNavigator,
    useLoginMountNavigation,
    usePostLoginFunction
} from "./auth.ui.hooks"
import type {
    TAuthFormRenderInputsPropsFunction,
    TAuthFormRenderInputsPropsFunctionParams,
    TAuthFormValues,
    TAuthLoginLocationState
} from "./auth.ui.types"

const localStorageService: StorageService = new StorageService(EStorageServiceType.Local)
const sessionStorageService: StorageService = new StorageService(EStorageServiceType.Session)

const renderSubtitle: TEmptyRenderCallback = emptyRenderCallback

/**
 * @deprecated uses legacy stuff, rewrite A$AP
 */
const SsoModalBody: LazyExoticComponent<FC> = lazy(
    (): Promise<{ default: FC }> => import("@/components/login/SsoModalBody")
)
/**
 * @deprecated uses legacy stuff, rewrite A$AP
 */
const EmailModalBody: LazyExoticComponent<FC> = lazy(
    (): Promise<{ default: FC }> => import("@/components/login/EmailModalBody")
)
/**
 * @deprecated uses legacy stuff, rewrite A$AP
 */
const VerificationModalBody: LazyExoticComponent<FC> = lazy(
    (): Promise<{ default: FC }> => import("@/components/login/VerificationModalBody")
)

const classes = {
    signedInToast: (): string => "absolute top-[20%] [inset-inline:0] [margin:0_auto]",
    passwordReset: {
        container: (): string =>
            "mx-[15px] flex items-center gap-x-[3px] text-[12px] leading-[18px] text-gray-200 font-[300]",
        btn: (): string =>
            buildCssClassName(
                "text-blue-200",
                "![outline:none]",
                getLinkDecorationTwClasses({ color: "blue", offset: 3 })
            )
    }
}

const AuthLoginPage: FC = (): ReactElement => {
    useLoginMountNavigation()

    useErrorLoginNavigator()

    const { t }: IUseTranslation = useTranslation()

    const { state: locationState }: Location<TAuthLoginLocationState> = useLocation()

    const modalDispatcher: TModalDispatcher = useModalDispatcher()

    const [wasRegisteredToastSeen, setWasRegisteredToastSeen]: [boolean, (val: boolean) => void] =
        useSessionStorage<boolean>(SessionStorageServiceKeys.UsedRegistrationKey(), false)

    const postLoginHandler: TPostLoginFunction = usePostLoginFunction()

    useUnmount((): void => {
        setWasRegisteredToastSeen(true)
    })

    const {
        mutateAsync: loginMutator,
        isPending: isLoginMutationPending,
        error: loginMutationError,
        isError: isLoginMutationError
    }: TLoginMutation = useLoginMutation({
        async onSuccess(
            { data: respData }: TLoginMutationResponse,
            vars: TLoginMutationPayload,
            _ctxt: void
        ): Promise<void> {
            localStorageService.removeItem(LocalStorageServiceKeys.MainUserLoggedOut())
            sessionStorageService.removeItem(SessionStorageServiceKeys.UsedRegistrationKey())

            if ("tfa_key" in respData)
                modalDispatcher.showModal<TVerificationModalProps>(EModalId._Legacy_Participant_LoginVerification, {
                    email: vars.email,
                    tfa_key: respData.tfa_key as string,
                    onSubmit: postLoginHandler
                })
            else await postLoginHandler(respData)
        }
    })

    const { mutateAsync: ssoMutator }: TSsoMutation = useSsoMutation({
        async onSuccess({ data: respData }: TSsoMutationResponse, _vars: string, _ctxt: void): Promise<void> {
            if (!isEmpty(respData))
                modalDispatcher.showModal<TSsoModalProps>(EModalId._Legacy_Participant_Sso, {
                    data: respData
                })
        }
    })

    const renderInputsProps: TAuthFormRenderInputsPropsFunction = useCallback(
        (params: TAuthFormRenderInputsPropsFunctionParams): TInputProps[] => {
            const { onBlur: handleEmailBlur, ...restEmailRegister }: UseFormRegisterReturn<"email"> =
                params.register("email")

            const _isInternalServerError: boolean = isInternalServerError(loginMutationError)
            const _isTooManyRequestsError: boolean = isTooManyRequestsError(loginMutationError)
            const _isBadRequestError: boolean = isBadRequestError(loginMutationError)

            const _emailErrors: string[] = values(params.errors?.email?.types) as []
            const _passErrors: string[] = values(params.errors?.password?.types) as []

            if (_isTooManyRequestsError) _passErrors.push(loginMutationError.response?.data?.detail)
            if (_isBadRequestError) _passErrors.push(loginMutationError.response?.data?.non_field_errors?.[0])
            if (_isInternalServerError) _passErrors.push(t("auth.registerPage.generalErrorHint"))

            return [
                {
                    type: "email",
                    isRequired: true,
                    placeholder: t("auth.emailInput.placeholder"),
                    isDisabled: isLoginMutationPending || params.isSubmitting,
                    enterKeyHint: "done",
                    isError: Object.hasOwn(params.errors, "email") || isLoginMutationError,
                    errorMessages: _emailErrors,
                    withSpelling: false,
                    mode: "email",
                    autoComplete: "email",
                    onBlur: async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
                        await handleEmailBlur(e)

                        if (await params.trigger("email")) void ssoMutator(e.target.value)
                    },
                    ...restEmailRegister
                },
                {
                    type: "password",
                    isRequired: true,
                    placeholder: t("auth.passInput.login.placeholder"),
                    isDisabled: params.isSubmitting,
                    enterKeyHint: "done",
                    isError: Object.hasOwn(params.errors, "password") || isLoginMutationError,
                    errorMessages: _passErrors,
                    withSpelling: false,
                    mode: "text",
                    autoComplete: "on",
                    maxSymbols: 100,
                    ...params.register("password")
                }
            ]
        },
        [t, isLoginMutationError, isLoginMutationPending, loginMutationError, ssoMutator]
    )

    const renderFormPassResetHint: TEmptyRenderCallback = useCallback(
        (): ReactElement => (
            <Trans
                parent="p"
                i18nKey="auth.loginPage.passAction"
                className={classes.passwordReset.container()}
                components={{
                    button: (
                        <button
                            className={classes.passwordReset.btn()}
                            type="button"
                            onClick={withPressHandler((): void =>
                                modalDispatcher.showModal(EModalId._LegacyResetPassword)
                            )}
                            onKeyDown={withPressHandler((): void =>
                                modalDispatcher.showModal(EModalId._LegacyResetPassword)
                            )}
                            aria-label={t("auth.loginPage.passAction.ariaLabel")}
                        />
                    )
                }}
            />
        ),
        [t]
    )

    const handleFormSubmit: (values: TAuthFormValues) => Promise<void> = useCallback(
        async (values: TAuthFormValues): Promise<void> => {
            await loginMutator({ email: values.email, password: values.password })
        },
        [loginMutator]
    )

    return (
        <Fragment>
            <ErrorHint
                isVisible={!wasRegisteredToastSeen && locationState?.isAlreadyRegistered}
                containerClassName={classes.signedInToast()}
                text={t("auth.loginPage.registeredHint")}
            />

            <AuthContainer
                mode="login"
                renderSubtitle={renderSubtitle}
                renderInputsProps={renderInputsProps}
                renderFormPassResetHint={renderFormPassResetHint}
                onSubmit={handleFormSubmit}
            />

            <ModalContainer modalId={EModalId._Legacy_Participant_Sso} container={SsoModalBody} />

            <ModalContainer modalId={EModalId._LegacyResetPassword} container={EmailModalBody} />

            <ModalContainer
                modalId={EModalId._Legacy_Participant_LoginVerification}
                container={VerificationModalBody}
            />
        </Fragment>
    )
}

AuthLoginPage.displayName = "AuthLoginPage"

export { AuthLoginPage }
